import { mergeAttributes } from '@tiptap/core';
import { ListItem } from '../list-item';
import type { Node } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { setStoredTextStyleMark } from '../../helpers/textStyleHelpers';

export const CustomListItem = ListItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      color: {
        default: null,
        parseHTML: (element) => element.style.color || null,
        renderHTML: (attributes) => {
          if (!attributes.color) return {};
          return { style: `color: ${attributes.color}; --list-item-color: ${attributes.color}` };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['li', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  // Ensures typing in an empty list item inherits the marker color rather than the default.
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, _oldState, newState) {
          // skip if no transaction in this batch moved the cursor
          if (!transactions.some((tr) => tr.selectionSet)) return null;
          // skip if the selection is a range (user has text selected)
          if (!newState.selection.empty) return null;

          const $from = newState.selection.$from;
          let listItem: Node | null = null;
          for (let d = $from.depth; d > 0 && !listItem; d--) {
            if ($from.node(d).type.name === 'listItem') listItem = $from.node(d);
          }

          // skip if not a list item, or if the list item has no color set
          if (!listItem?.attrs.color) return null;

          // skip if the list item has text; only restore colour for empty list item.
          if (listItem.textContent) return null;

          // set the stored marks to the list item's color, so the next keystroke inherits it
          return newState.tr.setStoredMarks([newState.schema.marks.textStyle.create({ color: listItem.attrs.color })]);
        },
      }),
    ];
  },

  // when pressing Enter inside a list item, ensure the marker for list item has the appropriate color.
  addKeyboardShortcuts() {
    return {
      ...this.parent?.(),
      Enter: () => {
        if (!this.editor.isActive(this.name)) return false;

        const activeTextStyleAttrs = this.editor.getAttributes('textStyle');
        const activeColor: string | null = activeTextStyleAttrs.color ?? null;

        const { $from: $fromBefore } = this.editor.state.selection;
        const originalMarkerColor: string | null = $fromBefore.node($fromBefore.depth - 1).attrs.color ?? null;

        return this.editor
          .chain()
          .splitListItem(this.name)
          .command(({ tr }) => {
            const { $from } = tr.selection;
            const listItemNode = $from.node($from.depth - 1);
            const pos = $from.before($from.depth - 1);

            if ($from.nodeAfter) {
              // When splitting a list item: new item inherits the bullet point color of the original item
              tr.setNodeMarkup(pos, undefined, { ...listItemNode.attrs, color: originalMarkerColor });
            } else {
              // New empty list item: use active cursor color
              tr.setNodeMarkup(pos, undefined, { ...listItemNode.attrs, color: activeColor });
              if (activeColor !== null) {
                setStoredTextStyleMark(tr, this.editor.schema, activeTextStyleAttrs);
              }
            }
            return true;
          })
          .run();
      },
      Tab: () => {
        if (this.editor.commands.sinkListItem(this.name)) return true;

        return this.editor.isActive(this.name);
      },
    };
  },
});
